import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';
import SEO from '@/components/feature/SEO';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://floridahousesforsale.deals';

const contactSchema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact Amparo Jarquin – Florida Real Estate',
  description: 'Get in touch with Amparo Jarquin, licensed REALTOR® in West Palm Beach FL. Call, email, or fill out the request form for Florida real estate inquiries.',
  url: `${SITE_URL}/contact`,
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Contact', item: `${SITE_URL}/contact` },
    ],
  },
};

export default function ContactPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Contact Amparo Jarquin | Florida Real Estate Agent | (772) 626-8544"
        description="Contact Amparo Jarquin, licensed REALTOR® in West Palm Beach FL. Call (772) 626-8544 or email for help buying or selling Florida real estate."
        keywords="contact Florida realtor, Amparo Jarquin phone, West Palm Beach real estate agent contact"
        canonical="/contact"
        schema={contactSchema}
      />
      <Navbar />
      <section className="relative pt-36 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/4fb81a1535dba3abe40ab8cba20a8006.jpg"
            alt="Miami Florida"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-stone-900/60"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <p className="text-amber-400 text-xs font-semibold tracking-widest uppercase mb-3">Get In Touch</p>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Contact Amparo</h1>
          <p className="text-white/70 max-w-xl mx-auto text-base">We&apos;d love to hear from you. Reach out today!</p>
        </div>
      </section>

      <section className="py-20 max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Contact Info */}
          <div>
            <p className="text-amber-600 text-xs font-semibold tracking-widest uppercase mb-3">Contact Information</p>
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Let&apos;s Start a Conversation</h2>
            <div className="space-y-6 mb-10">
              {[
                { icon: 'ri-map-pin-2-line', label: 'Office Location', value: 'West Palm Beach Florida 33406\nServing All of Florida' },
                { icon: 'ri-phone-line', label: 'Phone', value: '(772) 626-8544' },
                { icon: 'ri-mail-line', label: 'Email', value: 'pslucie369@gmail.com' },
                { icon: 'ri-time-line', label: 'Office Hours', value: 'Mon–Fri: 9am – 4pm\nSat: 10am – 4pm' },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-amber-500 text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{item.label}</p>
                    <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-lg overflow-hidden border border-gray-100 h-64">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3592.5!2d-80.1918!3d25.7617!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88d9b0a20ec8c111%3A0xff96f271ddad4f65!2sMiami%2C%20FL!5e0!3m2!1sen!2sus!4v1680000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                title="Florida Homes for Sale location"
              ></iframe>
            </div>
          </div>

          {/* Quick Links */}
          <div className="bg-stone-50 rounded-lg p-10 border border-stone-100">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Ready to Take the Next Step?</h3>
            <p className="text-gray-500 text-sm leading-relaxed mb-8">Fill out our information request form and Amparo will get back to you within 24 hours.</p>
            <Link
              to="/request"
              className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer text-sm mb-4"
            >
              <i className="ri-file-list-3-line"></i>
              Fill Out Request Form
            </Link>
            <a
              href="tel:+17726268544"
              className="w-full flex items-center justify-center gap-2 px-8 py-4 border-2 border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-phone-line"></i>
              Call (772) 626-8544
            </a>
            <div className="mt-10 pt-8 border-t border-stone-200">
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-widest mb-4">Follow Amparo</p>
              <div className="flex gap-3">
                {['ri-facebook-fill', 'ri-instagram-line', 'ri-linkedin-fill', 'ri-youtube-line'].map((icon) => (
                  <a key={icon} href="#" className="w-10 h-10 flex items-center justify-center bg-white border border-gray-200 rounded-full hover:border-amber-500 hover:text-amber-500 transition-colors text-gray-500 cursor-pointer">
                    <i className={`${icon} text-sm`}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
